// hooks/useMembership.ts — read /membership/state and expose mutations.

import { microToDollars } from "../lib/money";
import { useCallback, useEffect, useState } from "react";
import {
  billingConfirmIntent,
  gBuyPack20,
  gBuySingle,
  membershipActivate,
  membershipCancel,
  membershipState,
  type ActivateResult,
  type MembershipStateView,
  type PurchaseResult,
} from "../lib/api";

export interface UseMembershipResult {
  state: MembershipStateView | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  activate: (acceptTerms: boolean) => Promise<ActivateResult | null>;
  cancel: () => Promise<MembershipStateView | null>;
  buySingle: () => Promise<PurchaseResult | null>;
  buyPack20: () => Promise<PurchaseResult | null>;
  /** Test/dev path — fires the mock-mode webhook for an intent. */
  confirmIntent: (intent_id: string) => Promise<void>;
}

export function useMembership(): UseMembershipResult {
  const [state, setState] = useState<MembershipStateView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await membershipState();
      setState(r.state);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const activate = useCallback(async (acceptTerms: boolean) => {
    setError(null);
    try {
      const r = await membershipActivate(acceptTerms);
      setState(r.state);
      return r;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, []);

  const cancel = useCallback(async () => {
    setError(null);
    try {
      const r = await membershipCancel();
      setState(r.state);
      return r.state;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, []);

  const buySingle = useCallback(async () => {
    setError(null);
    try {
      const r = await gBuySingle();
      // Optimistic: mutate the balance in place so the panel updates without
      // a full refresh round-trip. The next refresh resyncs anyway.
      // #142 -- the display string travels with the raw figure.
      setState((s) => (s ? {
        ...s,
        g_credits: { ...s.g_credits, balance: r.balance, balance_micro: r.balance,
          balance_display: microToDollars(r.balance) },
      } : s));
      return r;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, []);

  const buyPack20 = useCallback(async () => {
    setError(null);
    try {
      const r = await gBuyPack20();
      setState((s) => (s ? {
        ...s,
        g_credits: { ...s.g_credits, balance: r.balance, balance_micro: r.balance,
          balance_display: microToDollars(r.balance) },
      } : s));
      return r;
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      return null;
    }
  }, []);

  const confirmIntent = useCallback(async (intent_id: string) => {
    setError(null);
    try {
      await billingConfirmIntent(intent_id);
      await refresh();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [refresh]);

  return {
    state, loading, error, refresh,
    activate, cancel, buySingle, buyPack20, confirmIntent,
  };
}
